const TRACE_ERRORS = {
  TOO_FAR_FROM_ENDPOINT: "TOO_FAR_FROM_ENDPOINT",
  NO_DIRECTIONS: "NO_DIRECTIONS",
  STACK_LEVEL_TOO_DEEP: "STACK_LEVEL_TOO_DEEP",
  SPLIT_TOO_CLOSE: "SPLIT_TOO_CLOSE",
};

const CYCLING_MODE_MASK = 81;
const WALKING_MODE_MASK = 82;
const DRAW_LINES_MASK = 74;
const DEFAULT_COLOR = 255;
const ERROR_COLOR = 16711680;

const coursePointsFromPath = (path, points) => {
  const cps = [];
  for (let i = 0; i < path.instructions.length; i++) {
    const inst = path.instructions[i];
    const divKill = /<div.*?>(.*?)<\/div>/gi;
    const bKill = /<\/?b>/gi;
    const orderKill = /take the (.*?) (right|left)/gi;
    const wbrKill = /<wbr\/>/g;
    const notes = inst.n
      .replace(divKill, "")
      .replace(bKill, "")
      .replace(orderKill, "Turn $2")
      .replace(wbrKill, "\u200B");
    cps.push({
      x: points[inst.i].x,
      y: points[inst.i].y,
      i: inst.i,
      n: notes,
      t: inst.t,
    });
  }
  return cps;
};

const decodePath = (encoded) => {
  const len = encoded.length;
  let index = 0;
  const array = [];
  let lat = 0;
  let lng = 0;
  var surface = 0;
  var rc = 0;

  while (index < len) {
    let b;
    let shift = 0;
    let result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const deltaLat = result & 1 ? ~(result >> 1) : result >> 1;
    lat += deltaLat;

    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    const deltaLon = result & 1 ? ~(result >> 1) : result >> 1;
    lng += deltaLon;

    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    var deltaSurface = result & 1 ? ~(result >> 1) : result >> 1;
    surface += deltaSurface;

    shift = 0;
    result = 0;
    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);
    var deltaRC = result & 1 ? ~(result >> 1) : result >> 1;
    rc += deltaRC;

    array.push([lat * 1e-5, lng * 1e-5, surface * 1e-1, rc * 1e-1]);
  }
  return array;
};

const decodeTrackPoints = (path) => {
  return decodePath(path.points).map((coord) => ({
    x: coord[1],
    y: coord[0],
    S: coord[2],
    R: coord[3],
  }));
};

async function routeBetween(a, b, mode, experiment, lineColor) {
  const weighting = mode === "rwgpsbike" ? "rwgps" : "fastest";
  const res = await (
    await window.fetch(
      [
        `/mrp_route?`,
        `vehicle=${mode}`,
        `&weighting=${weighting}`,
        `&points=${a.y},${a.x};${b.y},${b.x}`,
        `&locale=${window.navigator.language}`,
        "&type=json",
        "&point_attrs=surface",
      ].join(""),
      { headers: { Accept: "application/json" } }
    )
  ).json();
  if (res.segments && res.segments[0]) {
    const path = res.segments[0];
    const trackPoints = decodeTrackPoints(path);
    const route = {
      coursePoints: coursePointsFromPath(path, trackPoints),
      trackPoints,
    };
    if (distanceBetween(a, route.trackPoints[0]) > 200) {
      route.error = TRACE_ERRORS.TOO_FAR_FROM_ENDPOINT;
    }
    route.trackPoints[route.trackPoints.length - 1].options =
      mode === "rwgpsbike" ? CYCLING_MODE_MASK : WALKING_MODE_MASK;
    route.trackPoints[route.trackPoints.length - 1].color = route.error ? ERROR_COLOR : lineColor;
    return route;
  } else {
    return {
      error: TRACE_ERRORS.NO_DIRECTIONS,
      trackPoints: [a, { x: b.x, y: b.y, options: DRAW_LINES_MASK, color: DEFAULT_COLOR }],
      coursePoints: [],
    };
  }
}

const distanceBetween = (pointA, pointB) => {
  const lat1 = (pointA.y * Math.PI) / 180;
  const lng1 = (pointA.x * Math.PI) / 180;
  const lat2 = (pointB.y * Math.PI) / 180;
  const lng2 = (pointB.x * Math.PI) / 180;
  const latDiff = lat1 - lat2;
  const lngDiff = lng1 - lng2;
  const radius = 6378100;
  const latTmp = Math.sin(latDiff / 2);
  const lngTmp = Math.sin(lngDiff / 2);
  const a = latTmp * latTmp + Math.cos(lat1) * Math.cos(lat2) * lngTmp * lngTmp;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return radius * c;
};

const pathLength = (points) => {
  let distance = 0;
  for (let i = 1; i < points.length; i++) {
    distance += distanceBetween(points[i - 1], points[i]);
  }
  return distance;
};

const scaleCoord = ({ x: lng, y: lat }) => {
  const width = 80000;
  const height = 40000;

  const x = (lng + 180) * (width / 360);

  const latRad = (lat * Math.PI) / 180;
  const mercN = Math.log(Math.tan(Math.PI / 4 + latRad / 2));
  const y = height / 2 - (width * mercN) / (Math.PI * 2);

  return { x, y };
};

const distanceToPath = (point, points) => {
  let minDist = Infinity;
  const scaledPoint = scaleCoord(point);
  const testX = scaledPoint.x;
  const testY = scaledPoint.y;
  let x0, x, y0, y, dx, dy, d, u, x2, y2, dist;
  for (let i = 1; i < points.length; i++) {
    const scaledA = scaleCoord(points[i - 1]);
    const scaledB = scaleCoord(points[i]);
    x0 = scaledA.x;
    y0 = scaledA.y;
    x = scaledB.x;
    y = scaledB.y;
    dx = x - x0;
    dy = y - y0;
    d = dx * dx + dy * dy;
    u = d === 0 ? 0 : ((testX - x) * dx + (testY - y) * dy) / d;
    x2 = x + u * dx;
    y2 = y + u * dy;
    dist = Math.pow(testX - x2, 2) + Math.pow(testY - y2, 2);
    if (dist < minDist) {
      const d1 = Math.pow(testX - x0, 2) + Math.pow(testY - y0, 2);
      const d2 = Math.pow(testX - x, 2) + Math.pow(testY - y, 2);
      if (d1 - dist + (d2 - dist) > d) {
        if (d1 < d2) {
          dist = d1;
        } else {
          dist = d2;
        }
      }
    }

    if (dist < minDist) {
      minDist = dist;
    }
  }
  return minDist;
};

const similarityByDistanceFromRoute = (points, test) => {
  const distances = points.map((point) => distanceToPath(point, test));
  let maxI = 0;
  for (let i = 1; i < distances.length; i++) {
    if (distances[i] > distances[maxI]) {
      maxI = i;
    }
  }
  return {
    maxDistance: distances[maxI],
    maxI,
  };
};

const isGood = (points, test, stackLevel, debugId) => {
  if (stackLevel > 12 || points.length < 2) {
    return {
      error: TRACE_ERRORS.STACK_LEVEL_TOO_DEEP,
    };
  }
  if (test.length < 2) {
    return {
      accept: false,
    };
  }

  const lengthFactor = Math.abs(pathLength(points) / pathLength(test) - 1);
  const distanceTest = similarityByDistanceFromRoute(points, test);

  const accept = lengthFactor < 0.1 && distanceTest.maxDistance < 0.1;

  // console.log('isGood = ', accept ? 'P' : 'F', debugId, distanceTest.maxDistance, points.length, test.length, stackLevel)

  return {
    accept,
    distanceTest,
    lengthFactor,
  };
};

const join = (left, right) => {
  if (!left) return right;
  const trackPoints = [];
  const coursePoints = [];
  left.trackPoints.forEach((tp) => {
    trackPoints.push(tp);
  });
  left.coursePoints.forEach((cp) => {
    coursePoints.push(cp);
  });

  const offset = trackPoints.length - 1; // subtracting 1 because we're skipping the first point on the right

  // skip first point because it is a duplicate of the last point on the left
  for (let i = 1; i < right.trackPoints.length; i++) {
    trackPoints.push(right.trackPoints[i]);
  }
  right.coursePoints.forEach((cp) => {
    cp.i += offset;
    coursePoints.push(cp);
  });
  return {
    trackPoints,
    coursePoints,
    errors: (left.errors || [])
      .concat(right.errors || [])
      .concat([left.error, right.error])
      .filter((e) => e),
  };
};

const hasError = (route) => route.error || (route.errors && route.errors.length > 0);

// Fetch a path between endpoints. If it all falls within expectations, we're done. If not, split route at point furthest from the test path and recurse.
// There are a few heuristics to collapse failures together and retry in walking mode, and finally fall back to using the path directly and not getting cues.
const traceRecur = async ({
  points,
  mode,
  onStatus,
  stoppedRef,
  experiment,
  lineColor = DEFAULT_COLOR,
  stackLevel = 1,
  debugId = "",
}) => {
  if (stoppedRef.current) return { error: "stopped", trackPoints: [], coursePoints: [] };
  onStatus(debugId);
  let route = await routeBetween(points[0], points[points.length - 1], mode, experiment, lineColor);
  if (route.error) {
    console.error(route.error);
    return route;
  }
  const quality = isGood(points, route.trackPoints, stackLevel, debugId);
  if (quality.error) {
    route.trackPoints[route.trackPoints.length - 1].color = DEFAULT_COLOR;
    route.error = quality.error;
    return route;
  }
  if (quality.accept) {
    return route;
  } else {
    let splitIndex = quality?.distanceTest?.maxI ?? Math.floor(points.length / 2);
    // In some cases the points we're testing are all enough off-course that we pick a really bad split point. Better to naively jump to midpoint in that case.
    if (distanceBetween(points[0], points[splitIndex]) < 50) {
      splitIndex = Math.floor(points.length / 2);
    }
    // But if we're at the point where even the midpoint is too close, give up
    if (distanceBetween(points[0], points[splitIndex]) < 50) {
      splitIndex = points.length / 2;
      route.error = TRACE_ERRORS.SPLIT_TOO_CLOSE;
      return route;
    } else {
      const leftPoints = points.slice(0, splitIndex + 1);
      const rightPoints = points.slice(splitIndex);
      let left = await traceRecur({
        points: leftPoints,
        mode,
        onStatus,
        stoppedRef,
        experiment,
        lineColor,
        stackLevel: stackLevel + 1,
        debugId: debugId + "◀",
      });
      let right = await traceRecur({
        points: rightPoints,
        mode,
        onStatus,
        stoppedRef,
        experiment,
        lineColor,
        stackLevel: stackLevel + 1,
        debugId: debugId + "▶",
      });
      const badModeLeft = hasError(left);
      const badModeRight = hasError(right);
      // Drop down to a less restrictive mode if bike mode can't get a good route, at the highest level (wait until left != right)
      if (badModeLeft !== badModeRight && mode === "rwgpsbike") {
        if (hasError(left)) {
          left = await traceRecur({
            points: leftPoints,
            mode: "foot",
            onStatus,
            stoppedRef,
            experiment,
            lineColor,
            stackLevel: stackLevel + 1,
            debugId: debugId + "◁",
          });
        }
        if (hasError(right)) {
          right = await traceRecur({
            points: rightPoints,
            mode: "foot",
            onStatus,
            stoppedRef,
            experiment,
            lineColor,
            stackLevel: stackLevel + 1,
            debugId: debugId + "▷",
          });
        }
      }
      // Drop down to lines if our lowest-restriction mode still can't get a good route
      if (mode === "foot") {
        if (hasError(left)) {
          leftPoints[leftPoints.length - 1].options = DRAW_LINES_MASK;
          const trackPoints = leftPoints.map((p) => {
            p["S"] = 99;
            return p;
          }); // 99 is UNKNOWN
          left = { trackPoints, coursePoints: [], errors: [] };
        }
        if (hasError(right)) {
          rightPoints[rightPoints.length - 1].options = DRAW_LINES_MASK;
          const trackPoints = rightPoints.map((p) => {
            p["S"] = 99;
            return p;
          }); // 99 is UNKNOWN
          right = { trackPoints, coursePoints: [], errors: [] };
        }
      }
      return join(left, right);
    }
  }
};

export const autotrace = async (_route, onStatus, stoppedRef, experiment, lineColor) => {
  const route = _route.toSaveObj();
  const tracedRoute = await traceRecur({
    points: route.track_points.filter((pt) => pt.x && pt.y),
    mode: "rwgpsbike",
    onStatus,
    stoppedRef,
    experiment,
    lineColor,
  });
  if (!hasError(tracedRoute)) {
    return tracedRoute;
  } else {
    console.log(tracedRoute);
  }
};
